import { toast } from "@/hooks/useToast";
import { CategoryItem, NestedItem, Section, VariantConfig } from "./types";

// ============================================================================
// Toast Helpers
// ============================================================================

export const showErrorToast = (description: string) => {
  toast({ title: "Error", description, variant: "destructive" });
};

export const showSuccessToast = (description: string) => {
  toast({ title: "Success", description });
};

export const showValidationErrorToast = () => {
  toast({
    title: "Validation Error",
    description: "Please fix the errors",
    variant: "destructive",
  });
};

// ============================================================================
// Field Helpers
// ============================================================================

export const buildDefaultFieldValues = (fieldsConfig: any[]): Record<string, any> => {
  const defaults: Record<string, any> = {};
  fieldsConfig.forEach((field: any) => {
    if (field.filedType === "TEXT_BLOCK") return;
    if (field.defaultValue !== undefined) {
      defaults[field.fieldName] = field.defaultValue;
    } else if (field.value !== undefined) {
      defaults[field.fieldName] = field.value;
    } else {
      defaults[field.fieldName] = "";
    }
  });
  return defaults;
};

export const extractFieldsFromData = (
  data: Record<string, any>,
  fieldsConfig: any[]
): Record<string, any> => {
  const fields: Record<string, any> = {};
  fieldsConfig.forEach((field: any) => {
    if (field.filedType === "TEXT_BLOCK") return;
    const fieldValue = data[field.fieldName];
    fields[field.fieldName] =
      fieldValue !== undefined && fieldValue !== null ? fieldValue : (field.defaultValue ?? "");
  });
  return fields;
};

export const mapFieldsConfigToFormConfig = (fieldsConfig: any[]): any[] => {
  return fieldsConfig.map((f: any) => ({
    ...f,
    label: f.header || f.label || f.fieldName,
    fieldName: f.fieldName,
    valueType: f.valueType,
    filedType: f.filedType,
    placeholder: f.placeholder,
    required: f.required,
    defaultValue: f.value,
  }));
};

// ============================================================================
// Category Normalization Helpers
// ============================================================================

export const normalizeCategoryItems = (categories: any[]): CategoryItem[] => {
  return categories.map((cat: any) => {
    if (cat.faqs && Array.isArray(cat.faqs)) {
      return { ...cat, items: cat.faqs };
    }
    if (cat.profiles && Array.isArray(cat.profiles)) {
      return { ...cat, items: cat.profiles };
    }
    if (cat.cards && Array.isArray(cat.cards)) {
      return { ...cat, items: cat.cards };
    }
    if (cat.subjects && Array.isArray(cat.subjects)) {
      return { ...cat, items: cat.subjects };
    }
    return cat;
  });
};

export const processThreeLevelCategories = (categories: any[]): CategoryItem[] => {
  return categories.map((cat: any) => {
    if (cat.semesters && Array.isArray(cat.semesters)) {
      const semestersWithItems = cat.semesters.map((semester: any) => {
        if (semester.subjects && Array.isArray(semester.subjects)) {
          return { ...semester, items: semester.subjects };
        }
        if (semester.items && Array.isArray(semester.items)) {
          return { ...semester };
        }
        return { ...semester, items: [] };
      });
      return { ...cat, semesters: semestersWithItems };
    }
    return { ...cat, semesters: [] };
  });
};

// ============================================================================
// Expand State Helpers
// ============================================================================

export const getExpandedCategoryIds = (categories: CategoryItem[]): Set<number> => {
  const expandedSet = new Set<number>();
  categories.forEach((cat) => {
    const hasItems = cat.items && Array.isArray(cat.items) && cat.items.length > 0;
    const hasSemesters = cat.semesters && Array.isArray(cat.semesters) && cat.semesters.length > 0;
    if (hasItems || hasSemesters) {
      expandedSet.add(cat.id);
    }
  });
  return expandedSet;
};

export const getExpandedNestedIds = (categories: CategoryItem[]): Set<number> => {
  const expandedSet = new Set<number>();
  categories.forEach((cat) => {
    if (cat.semesters) {
      cat.semesters.forEach((semester: NestedItem) => {
        if (semester.items && semester.items.length > 0) {
          expandedSet.add(semester.id);
        }
      });
    }
  });
  return expandedSet;
};

// ============================================================================
// API Response Extractors
// ============================================================================

/**
 * Admin endpoints return `{ success: true, data: T | null }` on the axios `data` body.
 * Using `response?.data || response` breaks when `data` is null (falsy): we must not fall back
 * to the envelope object or Redux / forms receive `{ success, data }` instead of the entity.
 */
export const unwrapAdminApiPayload = <T = unknown>(response: any): T | null | undefined => {
  if (response == null) return response;
  if (typeof response === "object" && "success" in response && "data" in response) {
    return response.data as T;
  }
  return response as T;
};

/**
 * Admin API helpers (e.g. `@/api/admin/visionaries`) catch axios errors and return
 * `{ status, message }` or `null` instead of throwing — callers must verify success.
 * Successful JSON bodies use `APIResponse`: `{ success: true, data: T }`.
 */
export function isAdminApiEnvelopeSuccess(
  response: unknown
): response is { success: true; data?: unknown } {
  return (
    response != null &&
    typeof response === "object" &&
    "success" in response &&
    (response as { success?: boolean }).success === true
  );
}

export function getAdminApiFailureMessage(response: unknown, fallback = "Request failed"): string {
  if (response != null && typeof response === "object" && "message" in response) {
    const m = (response as { message?: unknown }).message;
    if (typeof m === "string" && m.trim().length > 0) return m;
  }
  return fallback;
}

export const extractCategoriesFromResponse = (
  response: any,
  childItemsFieldName: string = "categories"
): { categories: any[]; parentId: number | null; componentData: any } => {
  const unwrapped = unwrapAdminApiPayload(response);
  const data: any =
    unwrapped == null
      ? {}
      : Array.isArray(unwrapped)
        ? unwrapped
        : typeof unwrapped === "object"
          ? unwrapped
          : {};

  // Check different response structures
  const extractionStrategies = [
    {
      match: () => Array.isArray(data),
      extract: () => ({ categories: data, parentId: null, componentData: {} }),
    },
    {
      match: () => data[childItemsFieldName] && Array.isArray(data[childItemsFieldName]),
      extract: () => ({
        categories: data[childItemsFieldName],
        parentId: data.id || null,
        componentData: data,
      }),
    },
    {
      match: () => data.categories && Array.isArray(data.categories),
      extract: () => ({
        categories: data.categories,
        parentId: data.id || null,
        componentData: data,
      }),
    },
    {
      match: () => data.semesters && Array.isArray(data.semesters),
      extract: () => ({
        categories: data.semesters,
        parentId: data.id || null,
        componentData: data,
      }),
    },
  ];

  const strategy = extractionStrategies.find((s) => s.match());
  if (strategy) {
    return strategy.extract();
  }
  // Parent payloads (e.g. Visionaries with only pool_profiles and no categories key)
  if (data && typeof data === "object" && data.id != null) {
    const fromChild = data[childItemsFieldName];
    const cats = Array.isArray(fromChild)
      ? fromChild
      : Array.isArray(data.categories)
        ? data.categories
        : [];
    return {
      categories: cats,
      parentId: data.id || null,
      componentData: data,
    };
  }
  return { categories: [], parentId: null, componentData: {} };
};

// ============================================================================
// Section Preview Data Helper
// ============================================================================

export const prepareSectionPreviewData = (
  selectedSection: Section,
  componentId: number | string,
  componentFields: Record<string, any>,
  categories: any[],
  childItemsFieldName: string = "categories"
): Section => {
  return {
    ...selectedSection,
    componentId: String(componentId),
    data: {
      ...componentFields,
      id: componentId,
      [childItemsFieldName]: categories,
      categories: categories,
      semesters: childItemsFieldName === "semesters" ? categories : undefined,
    },
  };
};

// ============================================================================
// Payload Builders
// ============================================================================

/** DB `status` (baseFields) is boolean/TINYINT; reject junk like HTTP codes from merged error objects. */
const coerceBaseRowStatus = (value: unknown): boolean | undefined => {
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  return undefined;
};

export const buildSavePayload = (
  fields: Record<string, any>,
  pageId: string | undefined,
  currentUserId: string | number | undefined,
  isUpdate: boolean,
  additionalFields: Record<string, any> = {}
): any => {
  const { id, status: rawStatus, ...fieldsWithoutId } = fields;
  const status = coerceBaseRowStatus(rawStatus);
  return {
    ...fieldsWithoutId,
    ...(status !== undefined ? { status } : {}),
    page_id: Number(pageId),
    ...(isUpdate ? { updated_by: currentUserId } : { created_by: currentUserId }),
    ...additionalFields,
  };
};

export const isValidId = (id: string | number | undefined | null): boolean => {
  if (id === undefined || id === null) return false;
  const numId = Number(id);
  return !isNaN(numId) && numId > 0;
};

